import { ContentContainer } from "@/components/ContentContainer";
import MarkdownContent from "@/components/MarkdownContent";
import { NetworkAboutFeatureCard } from "@/components/network";
import { StrapiImageAsset } from "@/components/StrapiImageAsset";
import TextFieldStyled from "@/components/TextFieldStyled";
import AppContext from "@/context/AppContext";
import { sendContact } from "@/lib/api";
import theme from "@/styles/theme";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { grey } from "@mui/material/colors";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Typography from "@mui/material/Typography";
import Link from "next/link";
import React, { useContext, useState } from "react";
import PageHead from "@/components/PageHead";

export default function About({page}) {
  const { setSnackbar } = useContext(AppContext);
  const [ formValues, setFormValues ] = useState({});

  const handleFormValueChanged = (event) => {
    const { name, value } = event.target;
    setFormValues({...formValues, [name]: value});
  }

  const handleSubmit = async(event) => {
    event.preventDefault();

    await sendContact(formValues);
    setSnackbar("Deine Anfrage wurde versendet.");
    setFormValues({});
  }

  return <>
    <PageHead page={page} />

    <ContentContainer headline={page.title} image={page?.hero_image?.formats?.large?.url}>
      <Typography
        variant="h3"
        align="center"
        color="common.black"
        component="p"
        sx={{
          mb: 3,
          wordBreak: "break-all",
          [theme.breakpoints.up("md")]: {
            mb: 6,
          },
        }}
      >
        {page.content_headline}
      </Typography>
      <Grid
        container
        sx={{
          [theme.breakpoints.up("sm")]: {
            float: "left",
            width: "35%",
          },
          mr: 2,
          mb: 2,
        }}
      >
        <Grid item xs={12}>
          <StrapiImageAsset
            strapiImageObject={page.content_image[0]}
            layout="responsive"
            objectFit="contain"
            width={400}
            height={225}
            fixedHeight={true}
          />
        </Grid>
      </Grid>
      <MarkdownContent input={page.content_text} />
      <Grid container>
        <Grid item xs={12}>
          <Card
            elevation={0}
            align="center"
            sx={{
              bgcolor: theme.palette.secondary.main,
              color: theme.palette.common.white,
              position: "relative",
              my: 3,
              [theme.breakpoints.up("sm")]: {
                mt: 6,
                mb: -6,
              },
              "&.MuiPaper-root": {
                transform: "skewX(-5deg)",
              },
              [theme.breakpoints.up("md")]: {
                gridColumn: "1 / span 3",
                display: "block",
              },
              [theme.breakpoints.up("xl")]: {
                mb: 0,
                gridColumn: "auto",
              },
            }}
          >
            <Grid
              container
              alignItems="center"
              sx={{transform: "skewX(5deg)"}}
            >
              <Grid item xs={12}>
                <CardContent>
                  <Grid container justifyContent="center" alignItems="center">
                    <Grid item xs={12}>
                      <Typography
                        variant="h3"
                        color="common.white"
                        align="center"
                        sx={{mb: 3}}
                      >
                        Du hast Fragen? Unsere FAQs antworten!
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body1" align="center">
                        In unseren FAQs beantworten wir dir alle Fragen über
                        showcase.nrw. Warum sollte ich mich anmelden? Wie
                        funktionieren hier die Likes? Kostet mich das ganze
                        etwas? (Spoiler: Nein!)
                      </Typography>
                    </Grid>
                    <Grid xl={12}>
                      <Box
                        sx={{
                          zIndex: "0",
                          position: "relative",
                        }}
                      >
                        <Button
                          component={Link}
                          href="/netzwerk/ueber-uns/faq"
                          variant="outlined-white-x"
                          sx={{
                            mt: 2,
                            mb: 1.5,
                          }}
                        >
                          Zu den FAQs
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </ContentContainer>
    <Box
      sx={{
        "& .MuiContainer-root": {
          "&:nth-of-type(odd)": {
            "& > .MuiGrid-root": {
              backgroundColor: grey[100],
            },
          },
          "&:nth-of-type(even)": {
            "& > .MuiGrid-root": {
              backgroundColor: theme.palette.common.white,
            },
          },
          "&:last-of-type": {
            "& > .MuiGrid-root": {
              mb: 3,
              pt: 6,
              pb: 4,
              [theme.breakpoints.up("md")]: {
                pb: 12,
                mb: 14,
              },
            },
          },
        },
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          mt: -3,
          [theme.breakpoints.up("sm")]: {
            mt: -14,
            zIndex: "999",
          },
        }}
      >
        <Grid container justifyContent="center" sx={{py: 6}}>
          <Grid item xs={10}>
            <Typography
              variant="h3"
              align="center"
              color="common.black"
              component="p"
              sx={{mb: 6}}
            >
              showcase.nrw wird betrieben von
            </Typography>
            <Grid container spacing={3}>
              {page.about_provider.map((provider, idx) => {
                return (
                  <Grid item xs={12} md={6} key={idx}>
                    <NetworkAboutFeatureCard
                      image={provider.image[0]}
                      text={provider.text}
                      buttontext={provider.button_label}
                      link={provider.button_link}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
      </Container>
      <Container maxWidth="xl">
        <Grid
          container
          justifyContent="center"
          sx={{
            py: 3,
            [theme.breakpoints.up("md")]: {
              py: 6,
            },
          }}
        >
          <Grid item xs={10}>
            <Typography
              variant="h3"
              align="center"
              color="common.black"
              component="p"
              sx={{mb: 6}}
            >
              Kontakt
            </Typography>
            <form onSubmit={handleSubmit}>
              <input type="hidden" name="token" value="" />
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextFieldStyled
                    required={true}
                    type="text"
                    name="name"
                    label="Vor- / Nachname"
                    placeholder="Dein Name"
                    value={formValues.name ?? ''}
                    onChange={handleFormValueChanged}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextFieldStyled
                    required={true}
                    type="email"
                    name="email"
                    label="E-Mail Adresse"
                    placeholder="Deine E-Mail Adresse"
                    value={formValues.email ?? ''}
                    onChange={handleFormValueChanged}
                  />
                </Grid>
                <Grid item xs={12} className="textarea">
                  <label>Nachricht</label>
                  <TextareaAutosize
                    aria-label="textarea"
                    placeholder="Deine Nachricht"
                    name="message"
                    minRows={8}
                    style={{width: "100%"}}
                    value={formValues.message ?? ''}
                    onChange={handleFormValueChanged}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    type="submit"
                    sx={{mt: 4}}
                  >
                    Nachricht abschicken
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Container>
    </Box>
  </>;
}

export const getStaticProps = async() => {
  const pageResponse = await fetch(`${process.env.NEXT_PUBLIC_API_URL || "http://localhost:1337"}/page-about`);
  const page = await pageResponse.json();

  return {
    props: {
      page
    },
    revalidate: parseInt(process.env.SSG_REVALIDATE_TIMEOUT)
  }
}
